import React, { useState, useEffect } from 'react';
import { Plus, X, Trash2, ChevronUp, ChevronDown, Edit2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step 5 — Customer Items page redesigned as the single place for managing
// items. The Add Item modal now has two modes ("Existing" / "New") so staff
// don't need to visit Manage Items separately when a brand-new item type
// arrives. Each linked row shows its rate inline and has an Edit Rate
// button alongside the existing reorder/delete buttons.

const fmtMoney = (v) => v == null ? '—' : `£${Number(v).toFixed(4).replace(/\.?0+$/, '')}`;

// ----- Auto-code preview (mirrors backend logic) -----
const previewCode = (description, allItems) => {
  if (!description) return '';
  const firstWord = description.trim().split(/\s+/)[0] || '';
  const letters = (firstWord.match(/[A-Za-z]/g) || []).join('').slice(0, 3).toUpperCase();
  const prefix = (letters + 'XXX').slice(0, 3);
  // find max existing code with this prefix
  const re = new RegExp(`^${prefix}(\\d+)$`);
  let max = 0;
  (allItems || []).forEach(it => {
    const m = re.exec(it.code || '');
    if (m) max = Math.max(max, parseInt(m[1], 10));
  });
  return `${prefix}${String(max + 1).padStart(3, '0')}`;
};

const CustomerItems = () => {
  const [customerItems, setCustomerItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [movingMappingId, setMovingMappingId] = useState(null);

  // Add Item modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addCustomer, setAddCustomer] = useState(null);
  const [addMode, setAddMode] = useState('new');  // 'existing' | 'new'  (Step 5b iter: default to new — most common case while staff catch up customer catalogues)
  const [addExistingItemId, setAddExistingItemId] = useState('');
  const [addNewDescription, setAddNewDescription] = useState('');
  const [addNewCode, setAddNewCode] = useState('');
  const [addNewCodeOverride, setAddNewCodeOverride] = useState(false);
  const [addRate, setAddRate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Edit Rate modal
  const [showRateModal, setShowRateModal] = useState(false);
  const [rateMapping, setRateMapping] = useState(null);
  const [rateInput, setRateInput] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [customerItemsRes, itemsRes] = await Promise.all([
        fetch(`${API_URL}/api/operations/customer-items`, { credentials: 'include' }),
        fetch(`${API_URL}/api/operations/items`, { credentials: 'include' }),
      ]);
      if (customerItemsRes.ok && itemsRes.ok) {
        setCustomerItems(await customerItemsRes.json());
        setAllItems(await itemsRes.json());
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const flashError = (msg) => { setError(msg); setSuccess(''); setTimeout(() => setError(''), 5000); };
  const flashSuccess = (msg) => { setSuccess(msg); setError(''); setTimeout(() => setSuccess(''), 3000); };

  // ----- Open Add modal -----
  const openAdd = (customerData) => {
    setAddCustomer(customerData);
    setAddMode('new');
    setAddExistingItemId('');
    setAddNewDescription('');
    setAddNewCode('');
    setAddNewCodeOverride(false);
    setAddRate('');
    setError('');
    setSuccess('');
    setShowAddModal(true);
  };

  // Items not yet linked to this customer (for the existing dropdown)
  const itemsAvailableToLink = () => {
    if (!addCustomer) return [];
    const linkedIds = new Set(addCustomer.items.map(it => it.id));
    return allItems.filter(it => !linkedIds.has(it.id) && it.active !== false);
  };

  // Auto-update code preview as description changes (unless user has overridden)
  useEffect(() => {
    if (!addNewCodeOverride && addMode === 'new') {
      setAddNewCode(previewCode(addNewDescription, allItems));
    }
  }, [addNewDescription, allItems, addNewCodeOverride, addMode]);

  // ----- Submit Add -----
  const submitAdd = async () => {
    setError('');
    if (!addRate || Number.isNaN(Number(addRate)) || Number(addRate) < 0) {
      flashError('Rate is required and must be a non-negative number.');
      return;
    }
    setSubmitting(true);
    try {
      let itemId = null;
      if (addMode === 'existing') {
        if (!addExistingItemId) { flashError('Please pick an item.'); setSubmitting(false); return; }
        itemId = parseInt(addExistingItemId, 10);
      } else {
        // Create the item first
        const desc = addNewDescription.trim();
        if (!desc) { flashError('Please enter a description for the new item.'); setSubmitting(false); return; }
        const codeOverride = addNewCodeOverride ? addNewCode.trim().toUpperCase() : null;
        const itemRes = await fetch(`${API_URL}/api/operations/items`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ description: desc, code: codeOverride || undefined, active: true }),
        });
        if (!itemRes.ok) {
          const e = await itemRes.json().catch(() => ({}));
          throw new Error(e.detail || `Item create failed (${itemRes.status})`);
        }
        const newItem = await itemRes.json();
        itemId = newItem.id;
      }
      // Link to customer with rate
      const linkRes = await fetch(`${API_URL}/api/operations/customer-items`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: addCustomer.customer.id,
          item_id: itemId,
          rate_ex_vat: Number(addRate),
        }),
      });
      if (!linkRes.ok) {
        const e = await linkRes.json().catch(() => ({}));
        throw new Error(e.detail || `Link failed (${linkRes.status})`);
      }
      flashSuccess('Item added.');
      setShowAddModal(false);
      await fetchData();
    } catch (err) {
      flashError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Edit Rate -----
  const openRate = (mapping, item) => {
    setRateMapping({ ...mapping, _item: item });
    setRateInput(mapping.rate_ex_vat == null ? '' : String(mapping.rate_ex_vat));
    setShowRateModal(true);
  };
  const submitRate = async () => {
    if (rateInput === '' || Number.isNaN(Number(rateInput)) || Number(rateInput) < 0) {
      flashError('Rate must be a non-negative number.');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/operations/customer-items/${rateMapping.id}/rate`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rate_ex_vat: Number(rateInput) }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `Update failed (${res.status})`);
      }
      flashSuccess('Rate updated.');
      setShowRateModal(false);
      await fetchData();
    } catch (err) {
      flashError(err.message);
    }
  };

  // ----- Move + Remove -----
  const handleMoveItem = async (mappingId, direction) => {
    setMovingMappingId(mappingId);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/operations/customer-items/${mappingId}/move?direction=${direction}`,
        { method: 'PUT', credentials: 'include' });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `Move failed (${res.status})`);
      }
      await fetchData();
    } catch (err) {
      flashError(err.message);
    } finally {
      setMovingMappingId(null);
    }
  };
  const handleRemoveItem = async (customerId, mappingId) => {
    if (!window.confirm('Remove this item from the customer? The item itself stays in the catalogue — only the link is removed.')) return;
    try {
      const res = await fetch(`${API_URL}/api/operations/customer-items/${mappingId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to remove item');
      flashSuccess('Item removed from customer.');
      await fetchData();
    } catch (err) {
      flashError(err.message);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-lg text-gray-600">Loading...</div></div>;
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: '"Cinzel", serif' }}>
          Customer Items
        </h1>
        <p className="text-gray-600 mt-2">
          Each customer's catalogue of items, with the rate they pay and the order items appear on their invoice.
          New item types can be created directly here — the master Manage Items list is updated automatically.
        </p>
      </div>

      {success && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg"><p className="text-green-700 text-sm">{success}</p></div>}
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2"><AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" /><p className="text-red-700 text-sm">{error}</p></div>}

      <div className="space-y-6">
        {customerItems.map((customerData) => {
          const itemsWithOrder = customerData.items.map(item => {
            const mapping = customerData.mappings.find(m => m.item_id === item.id);
            return { item, mapping, display_order: mapping?.display_order ?? 99999 };
          }).sort((a, b) => a.display_order - b.display_order || (a.item.code || '').localeCompare(b.item.code || ''));
          return (
            <div key={customerData.customer.id} className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">{customerData.customer.name}</h2>
                  <p className="text-gray-600 text-sm">{customerData.customer.code}</p>
                </div>
                <button onClick={() => openAdd(customerData)}
                  className="flex items-center px-3 py-2 bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332] text-sm">
                  <Plus size={16} className="mr-1" /> Add Item
                </button>
              </div>

              {itemsWithOrder.length === 0 ? (
                <p className="text-gray-500 italic">No items linked to this customer</p>
              ) : (
                <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                  {itemsWithOrder.map(({ item, mapping }, idx) => {
                    const isFirst = idx === 0;
                    const isLast = idx === itemsWithOrder.length - 1;
                    const isMoving = mapping?.id && movingMappingId === mapping.id;
                    const rateMissing = mapping?.rate_ex_vat == null;
                    return (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-white hover:bg-gray-50">
                        <div className="flex items-center flex-1">
                          <span className="text-xs text-gray-400 font-mono w-8 text-right mr-3">{mapping?.display_order ?? '—'}</span>
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">{item.code}</p>
                            <p className="text-sm text-gray-600">{item.description}</p>
                          </div>
                          <div className="text-sm mr-4">
                            {rateMissing ? (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold">RATE NOT SET</span>
                            ) : (
                              <span className="font-mono text-gray-700">{fmtMoney(mapping.rate_ex_vat)}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => openRate(mapping, item)}
                            disabled={!mapping?.id}
                            title="Edit rate"
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => handleMoveItem(mapping?.id, 'up')}
                            disabled={isFirst || isMoving || !mapping?.id}
                            title="Move up"
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30">
                            <ChevronUp size={16} />
                          </button>
                          <button onClick={() => handleMoveItem(mapping?.id, 'down')}
                            disabled={isLast || isMoving || !mapping?.id}
                            title="Move down"
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30">
                            <ChevronDown size={16} />
                          </button>
                          <button onClick={() => handleRemoveItem(customerData.customer.id, mapping?.id)}
                            title="Remove from customer"
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded ml-2">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ============ Add Item Modal ============ */}
      {showAddModal && addCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h2 className="text-xl font-bold text-gray-800" style={{ fontFamily: '"Cinzel", serif' }}>Add Item</h2>
                <p className="text-sm text-gray-600 mt-1">{addCustomer.customer.name}</p>
              </div>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-gray-100 rounded">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}

              {/* Mode toggle */}
              <div className="flex border rounded-lg overflow-hidden text-sm">
                <button onClick={() => setAddMode('existing')}
                  className={`flex-1 px-4 py-2 ${addMode === 'existing' ? 'bg-[#1F2D3D] text-white' : 'bg-white text-gray-700'}`}>
                  Existing item
                </button>
                <button onClick={() => setAddMode('new')}
                  className={`flex-1 px-4 py-2 ${addMode === 'new' ? 'bg-[#1F2D3D] text-white' : 'bg-white text-gray-700'}`}>
                  New item
                </button>
              </div>

              {addMode === 'existing' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pick an item from the catalogue</label>
                  <select value={addExistingItemId} onChange={(e) => setAddExistingItemId(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg">
                    <option value="">— select —</option>
                    {itemsAvailableToLink().map(it => (
                      <option key={it.id} value={it.id}>{it.code} · {it.description}</option>
                    ))}
                  </select>
                  {itemsAvailableToLink().length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">All catalogue items are already linked to this customer. Use "New item" to create a new one.</p>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <input type="text" value={addNewDescription}
                      onChange={(e) => setAddNewDescription(e.target.value)}
                      placeholder="e.g. NAPKIN BLACK"
                      className="w-full px-3 py-2 border rounded-lg" />
                    <p className="text-xs text-gray-500 mt-1">This will be added to the master items catalogue and linked to {addCustomer.customer.name}.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code
                      <span className="font-normal text-gray-500 ml-2">(auto-suggested)</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <input type="text" value={addNewCode}
                        onChange={(e) => { setAddNewCode(e.target.value.toUpperCase()); setAddNewCodeOverride(true); }}
                        disabled={!addNewCodeOverride}
                        className="flex-1 px-3 py-2 border rounded-lg font-mono disabled:bg-gray-50"
                        placeholder="(type a description above)" />
                      <button type="button"
                        onClick={() => setAddNewCodeOverride(!addNewCodeOverride)}
                        className="text-xs px-3 py-2 border rounded-lg hover:bg-gray-50">
                        {addNewCodeOverride ? 'Use auto' : 'Edit'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">First 3 letters of the description + next sequential number. Codes are unique and internal.</p>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rate (£ ex VAT) <span className="text-red-600">*</span></label>
                <input type="number" step="0.0001" min="0" value={addRate}
                  onChange={(e) => setAddRate(e.target.value)}
                  placeholder="e.g. 0.27"
                  className="w-full px-3 py-2 border rounded-lg" />
                <p className="text-xs text-gray-500 mt-1">Rate is required. Will appear on this customer's invoices for this item.</p>
              </div>

              <div className="flex gap-2 pt-2">
                <button onClick={() => setShowAddModal(false)} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={submitAdd} disabled={submitting}
                  className="flex-1 px-4 py-2 bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332] disabled:opacity-50">
                  {submitting ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ Edit Rate Modal ============ */}
      {showRateModal && rateMapping && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowRateModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: '"Cinzel", serif' }}>Edit Rate</h2>
              <button onClick={() => setShowRateModal(false)} className="p-2 hover:bg-gray-100 rounded">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}
              <div>
                <p className="text-sm text-gray-600">{rateMapping._item?.code}  ·  {rateMapping._item?.description}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rate (£ ex VAT) <span className="text-red-600">*</span></label>
                <input type="number" step="0.0001" min="0" value={rateInput}
                  onChange={(e) => setRateInput(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') submitRate(); }} />
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setShowRateModal(false)} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={submitRate} className="flex-1 px-4 py-2 bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332]">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerItems;
